import { useState, useCallback, useRef, useEffect } from 'react';

enum ContentTypes {
  APPLICATION_JSON = 'application/json',
  MULTIPART_FORM_DATA = 'multipart/form-data',
  FORM_URLCODED = 'application/x-www-form-urlencoded',
}

type HttpMethod = 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestParams {
  endpoint: string; // absolute like "/api/..." or relative; will be prefixed by REACT_APP_BASE_URL if provided
  method?: HttpMethod;
  headers?: Record<string, string>;
  payload?: Record<string, any> | string | number | FormData | Blob;
  responseType?: 'json' | 'text' | 'blob';
}

type HttpError = {
  message: string;
  status?: number;
  details?: unknown;
};

export function useHttp() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const sendRequest = useCallback(
    async <T = unknown>(requestConfig: RequestParams, applyData?: (response: T) => void): Promise<{ data: T; redirected: boolean; url: string; status: number; }> => {
      const { endpoint, method = 'GET', headers, payload, responseType = 'json' } = requestConfig;

      const baseUrl = (typeof process !== 'undefined' ? (process.env as Record<string, string | undefined>).REACT_APP_BASE_URL : undefined) || (window as any)?.__BASE_URL__;
      const isAbsolute = endpoint.startsWith('http://') || endpoint.startsWith('https://');
      const normalizedBase = baseUrl ? baseUrl.replace(/\/$/, '') : '';
      const normalizedPath = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      const url = isAbsolute ? endpoint : (normalizedBase ? `${normalizedBase}/${normalizedPath}` : endpoint);

      const controller = new AbortController();
      abortControllerRef.current?.abort();
      abortControllerRef.current = controller;

      const requestHeaders: Record<string, string> = {
        ...(headers || {}),
      };

      const isJsonPayload = !(payload instanceof FormData) && !(payload instanceof Blob);
      if (isJsonPayload && payload !== undefined && requestHeaders['Content-Type'] === undefined) {
        requestHeaders['Content-Type'] = ContentTypes.APPLICATION_JSON;
      }

      setIsLoading(true);
      setError(null);

      try {
        const body =
          method === 'GET' || method === 'HEAD'
            ? undefined
            : payload instanceof FormData || payload instanceof Blob
              ? (payload as BodyInit)
              : payload !== undefined
                ? (requestHeaders['Content-Type'] === ContentTypes.APPLICATION_JSON
                    ? JSON.stringify(payload as Record<string, unknown>)
                    : (payload as unknown as BodyInit))
                : undefined;

        const res = await fetch(url, {
          method,
          headers: requestHeaders,
          body,
          signal: controller.signal,
          credentials: 'include',
        });

        const contentType = res.headers.get('content-type') || '';

        if (!res.ok) {
          let message = `Request failed with status ${res.status}`;
          let details: unknown = undefined;
          try {
            if (contentType.includes('application/json')) {
              const errJson = await res.json();
              message = (errJson?.message as string) || message;
              details = errJson;
            } else {
              const errText = await res.text();
              message = errText || message;
            }
          } catch {
            // ignore parse error
          }
          setError(message);
          const err: HttpError = { message, status: res.status, details };
          throw Object.assign(new Error(message), err);
        }

        let data: any;
        if (responseType === 'blob') {
          data = await res.blob();
        } else if (responseType === 'text' || contentType.includes('text/')) {
          data = await res.text();
        } else {
          data = contentType.includes('application/json') ? await res.json() : await res.text();
        }

        if (applyData) applyData(data as T);
        return { data: data as T, redirected: res.redirected, url: res.url, status: res.status };
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return { isLoading, error, sendRequest, cancel };
}
